import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { EmailChipInput } from '@/components';

export function ComposeModal({ isOpen, onClose, onSend }) {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [showToolbar, setShowToolbar] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const fileInputRef = useRef(null);
  const quillRef = useRef(null);

  const hasRecipient = useMemo(() => {
    if (Array.isArray(to)) {
      return to.some(
        (item) => typeof item === 'string' && item.trim().length > 0,
      );
    }
    if (typeof to === 'string') {
      return to.split(',').some((item) => item.trim().length > 0);
    }
    return false;
  }, [to]);

  const handleSend = useCallback(() => {
    if (!hasRecipient) {
      toast.error('Please specify at least one recipient.');
      return;
    }
    onSend({
      to,
      cc: showCc ? cc : '',
      bcc: showBcc ? bcc : '',
      subject,
      body,
      attachedFile: attachments[0] || null,
      attachments,
    });
    // Reset state after send
    setTo('');
    setCc('');
    setBcc('');
    setShowCc(false);
    setShowBcc(false);
    setSubject('');
    setBody('');
    setAttachments([]);
    setShowEmojiPicker(false);
  }, [
    hasRecipient,
    to,
    cc,
    bcc,
    showCc,
    showBcc,
    subject,
    body,
    attachments,
    onSend,
  ]);

  // Keyboard shortcut: Cmd/Ctrl + Enter to send
  useEffect(() => {
    if (!isOpen || isMinimized) return;
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        if (hasRecipient) {
          handleSend();
        }
      } else if (e.key === 'Escape') {
        if (showEmojiPicker) {
          setShowEmojiPicker(false);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isMinimized, handleSend, hasRecipient, showEmojiPicker]);

  if (!isOpen) return null;

  const handleAddFiles = (files) => {
    if (!files || files.length === 0) return;
    const newFiles = Array.from(files);
    setAttachments((prev) => [...prev, ...newFiles]);
  };

  const handleRemoveAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleAddFiles(e.dataTransfer.files);
    }
  };

  const handleInsertEmoji = (emoji) => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      const range = editor.getSelection(true);
      editor.insertText(range.index, emoji);
      editor.setSelection(range.index + emoji.length);
    } else {
      setBody((prev) => prev + emoji);
    }
    setShowEmojiPicker(false);
  };

  const QUICK_EMOJIS = [
    '👋',
    '👍',
    '😊',
    '🎉',
    '🚀',
    '💼',
    '✅',
    '🙏',
    '💡',
    '🔥',
  ];

  const modules = {
    toolbar: showToolbar
      ? [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ color: [] }, { background: [] }],
          [{ list: 'ordered' }, { list: 'bullet' }, { align: [] }],
          ['link', 'blockquote', 'code-block'],
          ['clean'],
        ]
      : false,
  };

  // Minimized Floating Pill View
  if (isMinimized) {
    return (
      <div
        className="fixed z-50 bottom-4 sm:bottom-6 right-4 sm:right-6 lg:right-10 w-72 sm:w-80 h-12 bg-[#EAF5E9] text-[#1E6555] rounded-xl shadow-[0_12px_36px_rgba(0,0,0,0.18)] flex items-center justify-between px-4 cursor-pointer hover:bg-[#dff0de] transition-all border border-[#CDE5CA]"
        onClick={() => setIsMinimized(false)}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="w-2.5 h-2.5 rounded-full bg-primary shrink-0 animate-pulse" />
          <span className="text-sm font-bold text-[#1E6555] truncate">
            {subject.trim() || 'New Message (Draft)'}
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsMinimized(false);
            }}
            className="p-1 text-[#1E6555]/70 hover:text-[#1E6555] hover:bg-black/5 rounded-lg transition-colors"
            title="Expand"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 15l7-7 7 7"
              />
            </svg>
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="p-1 text-[#1E6555]/70 hover:text-status-error hover:bg-status-error/10 rounded-lg transition-colors"
            title="Close"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`fixed z-50 flex flex-col bg-white shadow-[0_24px_60px_-12px_rgba(0,0,0,0.25),0_0_0_1px_rgba(0,0,0,0.08)] rounded-2xl overflow-hidden border border-[#CDE5CA] transition-all duration-200 ${
        isMaximized
          ? 'inset-4 sm:inset-6 md:inset-8'
          : 'bottom-4 sm:bottom-6 right-4 sm:right-6 lg:right-10 w-[calc(100vw-32px)] sm:w-[540px] md:w-[580px]'
      }`}
      style={{
        height: isMaximized ? 'auto' : '580px',
        maxHeight: isMaximized ? 'none' : 'calc(100vh - 64px)',
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* ── Top Header ─────────────────────────────────────────────── */}
      <div
        className="bg-[#EAF5E9] border-b border-[#D2EBD0] px-4 py-3 flex items-center justify-between cursor-pointer select-none shrink-0"
        onClick={() => setIsMinimized(true)}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-7 h-7 rounded-lg bg-white shadow-2xs flex items-center justify-center text-[#1E6555] border border-[#C6E6C4] shrink-0">
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <span className="text-sm font-bold text-[#1E6555] tracking-tight truncate">
            {subject.trim() || 'New Message'}
          </span>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsMinimized(true);
            }}
            className="p-1.5 text-[#1E6555]/70 hover:text-[#1E6555] hover:bg-black/5 rounded-lg transition-colors"
            title="Minimize"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M18 12H6"
              />
            </svg>
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsMaximized(!isMaximized);
            }}
            className="p-1.5 text-[#1E6555]/70 hover:text-[#1E6555] hover:bg-black/5 rounded-lg transition-colors"
            title={isMaximized ? 'Restore' : 'Maximize'}
          >
            {isMaximized ? (
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9V4.5M15 9h4.5M15 9l5.25-5.25M15 15v4.5M15 15h4.5M15 15l5.25 5.25"
                />
              </svg>
            ) : (
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75v4.5m0-4.5h-4.5m4.5 0L15 9M20.25 20.25v-4.5m0 4.5h-4.5m4.5 0L15 15"
                />
              </svg>
            )}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="p-1.5 text-[#1E6555]/70 hover:text-status-error hover:bg-status-error/10 rounded-lg transition-colors ml-1"
            title="Close"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* ── Main Body Container ──────────────────────────────────────── */}
      <div className="flex flex-col flex-1 overflow-hidden bg-white relative">
        {/* Drag & Drop Overlay */}
        {isDragging && (
          <div className="absolute inset-0 z-30 bg-emerald-50/90 border-2 border-dashed border-emerald-500 flex flex-col items-center justify-center pointer-events-none transition-all">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 mb-2 animate-bounce">
              <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
            </div>
            <p className="text-sm font-bold text-emerald-800">
              Drop files here to attach
            </p>
          </div>
        )}

        {/* ── To Field ──────────────────────────────────────────────── */}
        <div className="flex items-center px-4 py-2 border-b border-gray-100 min-h-[44px] gap-2.5">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider w-8 shrink-0">
            To
          </span>
          <div className="flex-1 min-w-0">
            <EmailChipInput
              value={to}
              onChange={setTo}
              placeholder="Recipient email address..."
              autoFocus
            />
          </div>
          <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 shrink-0">
            <button
              type="button"
              onClick={() => setShowCc((p) => !p)}
              className={`px-2 py-0.5 rounded-md transition-all ${
                showCc
                  ? 'bg-primary/15 text-primary font-bold shadow-2xs'
                  : 'hover:bg-gray-100 text-gray-600'
              }`}
            >
              Cc
            </button>
            <button
              type="button"
              onClick={() => setShowBcc((p) => !p)}
              className={`px-2 py-0.5 rounded-md transition-all ${
                showBcc
                  ? 'bg-primary/15 text-primary font-bold shadow-2xs'
                  : 'hover:bg-gray-100 text-gray-600'
              }`}
            >
              Bcc
            </button>
          </div>
        </div>

        {/* ── Cc Field ──────────────────────────────────────────────── */}
        {showCc && (
          <div className="flex items-center px-4 py-1.5 border-b border-gray-100 min-h-[40px] gap-2.5 bg-gray-50/40">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider w-8 shrink-0">
              Cc
            </span>
            <div className="flex-1 min-w-0">
              <EmailChipInput
                value={cc}
                onChange={setCc}
                placeholder="Cc recipients..."
              />
            </div>
          </div>
        )}

        {/* ── Bcc Field ─────────────────────────────────────────────── */}
        {showBcc && (
          <div className="flex items-center px-4 py-1.5 border-b border-gray-100 min-h-[40px] gap-2.5 bg-gray-50/40">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider w-8 shrink-0">
              Bcc
            </span>
            <div className="flex-1 min-w-0">
              <EmailChipInput
                value={bcc}
                onChange={setBcc}
                placeholder="Bcc recipients..."
              />
            </div>
          </div>
        )}

        {/* ── Subject Field ─────────────────────────────────────────── */}
        <div className="flex items-center px-4 py-2.5 border-b border-gray-100">
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full outline-none text-sm text-gray-900 font-semibold placeholder:text-gray-400 placeholder:font-normal"
            placeholder="Subject"
          />
        </div>

        {/* ── Body Quill Editor ─────────────────────────────────────── */}
        <div className="flex-1 overflow-hidden flex flex-col relative compose-quill-wrapper">
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={body}
            onChange={setBody}
            modules={modules}
            className="flex-1 overflow-y-auto h-full text-sm custom-scrollbar"
            placeholder="Write your message here..."
          />
        </div>

        {/* ── Attachments List ──────────────────────────────────────── */}
        {attachments.length > 0 && (
          <div className="px-4 py-2 border-t border-gray-100 flex flex-wrap gap-2 max-h-24 overflow-y-auto bg-gray-50/60 custom-scrollbar">
            {attachments.map((file, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-2.5 py-1 text-xs shadow-2xs group"
              >
                <div className="p-1 bg-emerald-50 text-emerald-600 rounded">
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                    />
                  </svg>
                </div>
                <div className="min-w-0 max-w-[150px]">
                  <p
                    className="font-medium text-gray-800 truncate"
                    title={file.name}
                  >
                    {file.name}
                  </p>
                  <p className="text-[10px] text-gray-400">
                    {formatFileSize(file.size)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveAttachment(idx)}
                  className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                  title="Remove attachment"
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* ── Footer Action Bar ─────────────────────────────────────── */}
        <div className="bg-white border-t border-gray-100 px-4 py-3 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Send Button */}
            <button
              type="button"
              onClick={handleSend}
              disabled={!hasRecipient}
              className={`px-5 py-2 text-sm font-semibold rounded-xl transition-all shadow-sm flex items-center gap-2 ${
                hasRecipient
                  ? 'bg-primary hover:bg-primary-hover text-white active:scale-95 cursor-pointer'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
              }`}
            >
              <span>Send</span>
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                />
              </svg>
            </button>

            {/* Hidden multiple file input */}
            <input
              type="file"
              ref={fileInputRef}
              multiple
              onChange={(e) => {
                if (e.target.files) {
                  handleAddFiles(e.target.files);
                  e.target.value = '';
                }
              }}
              className="hidden"
            />

            {/* Formatting Toggle */}
            <button
              type="button"
              onClick={() => setShowToolbar((p) => !p)}
              className={`p-2 rounded-lg transition-colors ${
                showToolbar
                  ? 'bg-emerald-50 text-emerald-700 font-bold'
                  : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
              }`}
              title="Toggle formatting toolbar"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16m-7 6h7"
                />
              </svg>
            </button>

            {/* Attach Files */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-gray-500 hover:text-gray-800 p-2 rounded-lg hover:bg-gray-100 transition-colors relative"
              title="Attach files (or drag & drop)"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                />
              </svg>
              {attachments.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-emerald-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {attachments.length}
                </span>
              )}
            </button>

            {/* Emoji Trigger */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowEmojiPicker((p) => !p)}
                className={`p-2 rounded-lg transition-colors ${
                  showEmojiPicker
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
                }`}
                title="Insert emoji"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </button>

              {/* Emoji Quick Picker Popover */}
              {showEmojiPicker && (
                <div className="absolute bottom-11 left-0 bg-white border border-gray-200 rounded-xl shadow-xl p-2 z-40 flex items-center gap-1">
                  {QUICK_EMOJIS.map((em) => (
                    <button
                      key={em}
                      type="button"
                      onClick={() => handleInsertEmoji(em)}
                      className="p-1.5 hover:bg-gray-100 rounded-lg text-base transition-transform hover:scale-125"
                    >
                      {em}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Side: Shortcut badge & Discard */}
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline-flex items-center gap-1 text-[11px] text-gray-400 font-medium select-none">
              <kbd className="bg-gray-100 border border-gray-200 text-gray-500 px-1.5 py-0.5 rounded text-[10px] font-mono">
                ⌘ / Ctrl + ↵
              </kbd>
            </span>
            <button
              type="button"
              onClick={() => {
                if (!to && !subject && !body && attachments.length === 0) {
                  onClose();
                } else if (window.confirm('Discard this draft?')) {
                  setTo('');
                  setCc('');
                  setBcc('');
                  setSubject('');
                  setBody('');
                  setAttachments([]);
                  onClose();
                }
              }}
              className="text-gray-400 hover:text-red-500 p-2 rounded-lg hover:bg-red-50 transition-colors"
              title="Discard draft"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
